/**
 * Shows the exact ServiceNow records that pass the current bounded selection.
 *
 * The backend supplies display values for review and canonical filter values
 * for click-to-filter. Keeping that translation at the provider boundary lets
 * administrators iterate without the browser reconstructing ServiceNow data.
 */
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { ServiceNowSelectionPreview as SelectionPreview } from '../types'

export function ServiceNowSelectionPreview({
  preview,
  loading,
  error,
  onFilter,
}: {
  preview: SelectionPreview | null
  loading: boolean
  error?: string
  onFilter: (field: string, value: string) => void
}) {
  if (!preview && loading) {
    return (
      <div role="status" className="flex h-24 items-center justify-center rounded-[9px] border border-slate-200 bg-white text-xs text-slate-500">
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Updating live preview…
      </div>
    )
  }
  if (!preview) {
    return error ? (
      <p role="alert" className="rounded-[9px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        {error}
      </p>
    ) : null
  }

  return (
    <section className="overflow-hidden rounded-[9px] border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-3 py-2">
        <div>
          <h4 className="text-xs font-semibold text-slate-800">Live preview</h4>
          <p className="text-[11px] text-slate-500">
            <strong className="font-semibold text-slate-800">{preview.matchCount}</strong>
            {' '}of {preview.windowCount} records match
          </p>
        </div>
        {loading && <Loader2 aria-label="Updating preview" className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </header>

      {preview.rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-slate-500">
          No records in this window match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] table-fixed text-left text-[11px]">
            <thead className="border-b border-slate-100 bg-white text-slate-500">
              <tr>
                {preview.columns.map(column => (
                  <th key={column.field} className={cn(
                    'px-3 py-2 font-medium',
                    column.field === 'short_description' ? 'w-[28%]' : 'w-auto',
                  )}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {preview.rows.map(row => (
                <tr key={row.sysId}>
                  {preview.columns.map(column => {
                    const filterField = column.filterField
                    const filterValue = filterField
                      ? row.filterValues[filterField]
                      : undefined
                    const value = row.values[column.field] || '—'
                    return (
                      <td key={column.field} className="truncate px-3 py-2" title={value}>
                        {filterField && filterValue ? (
                          <button
                            type="button"
                            aria-label={`Filter by ${column.label}: ${value}`}
                            className="max-w-full truncate border-b border-dotted border-slate-400 text-left hover:border-slate-900 hover:text-slate-950"
                            title={`Filter by ${column.label}: ${value}`}
                            onClick={() => onFilter(filterField, filterValue)}
                          >
                            {value}
                          </button>
                        ) : value}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-400">
        Showing the first {preview.rows.length} matching record{preview.rows.length === 1 ? '' : 's'}.
      </p>
    </section>
  )
}
